import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, ElementRef, Renderer2 } from '@angular/core';
import { Meta } from '@angular/platform-browser';
// import { HeaderComponent } from './components/header/header';
// import { NavigationComponent } from './components/navigation/navigation';
import { IonicModule } from '@ionic/angular';
import { environment } from '../environments/environment';
import { HoverComponent } from './components/shared/hover-component/hover-component';
import { ThemeService } from './shared/services/theme.service';
import { SwNavigatorService } from './shared/services/sw-navigator.service';
import { CustomModalController } from './shared/services/custom-modal-controller.service';
import { ScreenLayer } from './shared/enums/screen-layer.enum';
import { AuthService } from './shared/services/auth.service';
import { HttpClient } from '@angular/common/http';
import { PushNotificationService } from './shared/services/push-notification.service';
import { fetchAndPopulateMaterials } from './state/materials.state';
import { user, isAdmin } from './state/auth.state';
import { isDesktopViewport, VIEWPORT_BREAKPOINTS } from './shared/utils/viewport.utils';
import { SeoService } from './shared/services/seo.service';
import { NavbarVisibilityService } from './shared/services/navbar-visibility.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StorageService, STORAGE_KEYS } from './shared/services/storage.service';
import { BlurOnScrollService } from './shared/directives/blur-on-scroll.directive';
import { PlatformService } from './shared/services/platform.service';
import { NotificationService } from './shared/services/notification.service';
import { FloatingBadgeComponent } from './components/shared/floating-badge';
import { TranslatePipe } from './shared/pipes/translate.pipe';
import { TranslationService } from './shared/services/translation.service';
import { materialsState } from './state/materials.state';

@Component({
  selector: 'app-root',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    // HeaderComponent,
    // NavigationComponent,
    IonicModule,
    HoverComponent,
    FloatingBadgeComponent,
    TranslatePipe
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class App implements OnInit {
  protected readonly title = 'remateriar';
  private themeService = inject(ThemeService);
  private swnav = inject(SwNavigatorService);
  private customModalCtrl = inject(CustomModalController);
  private authService = inject(AuthService);
  protected readonly user = user;
  private initialViewportHeight: number = 0;
  private desktopBreakpoint: number = VIEWPORT_BREAKPOINTS.DESKTOP_MIN;
  private seoService = inject(SeoService);
  public navbarVisibility = inject(NavbarVisibilityService);
  private router = inject(Router);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private pushNotificationService = inject(PushNotificationService);
  private storage = inject(StorageService);
  private blurOnScrollService = inject(BlurOnScrollService);
  private meta = inject(Meta);
  private platform = inject(PlatformService);
  private notificationService = inject(NotificationService);
  private translationService = inject(TranslationService);
  private http = inject(HttpClient);

  // Expose isBrowser to template for SSR-safe rendering of Ionic components
  protected isBrowser = this.platform.isBrowser;

  // Notification badge
  protected unreadCount = this.notificationService.unreadCount;
  protected hasUnread = this.notificationService.hasUnread;

  // Admin check
  protected isAdmin = isAdmin;

  protected goToAdmin(): void {
    this.router.navigate(['/admin']);
  }

  /**
   * Triggers a haptic vibration feedback
   * Uses Web Vibration API if available
   */
  private triggerVibration(duration: number = 50): void {
    const nav = this.platform.navigator;
    if (nav && 'vibrate' in nav) {
      nav.vibrate(duration);
    }
  }

  /**
   * Handles tab click events with vibration feedback
   * If tab is already selected, scroll to top or toggle view (for home)
   * Prevents navigation to preserve current URL (e.g., /material/plastico stays instead of /material)
   */
  onTabClick(event: Event, isSelected: boolean): void {
    this.triggerVibration(20);
    if (isSelected) {
      // Prevent Ionic's default navigation to preserve current URL with slug
      event.preventDefault();
      event.stopPropagation();

      // Get current route to determine which tab
      const currentUrl = this.router.url;

      if (currentUrl.includes('/home')) {
        // For home tab: toggle between map and results if at bottom
        this.handleHomeTabClick();
      } else {
        // For other tabs: scroll to top
        this.scrollToTop();
      }

      // Toggle navigation for nano screens
      this.toggleNavigation(event);
    }
  }

  private static readonly TAB_LAYER_MAP: Record<string, ScreenLayer | null> = {
    home: ScreenLayer.Home,
    reciclar: null,
    material: null,
    profile: null,
  };

  onTabChange(event: { tab: string }): void {
    const layer = App.TAB_LAYER_MAP[event.tab];
    if (layer !== undefined && layer !== null) {
      this.customModalCtrl.demoteModalsForLayer(layer);
    } else {
      this.customModalCtrl.demoteAllModals();
    }
  }

  /**
   * Handle home tab click when already selected
   * Uses the home component's dedicated method for smart scroll/toggle behavior
   */
  private handleHomeTabClick(): void {
    const win = this.platform.window;
    if (!win) return;
    // Try to find the home component and call its handleHomeTabClick method
    const homeComponent = (win as any).homeComponentInstance;
    if (homeComponent && typeof homeComponent.handleHomeTabClick === 'function') {
      homeComponent.handleHomeTabClick();
    } else {
      // Fallback: scroll to top if home component not available
      this.scrollToTop();
    }
  }

  /**
   * Scroll the current page to top
   * Uses component instances for reliable scrolling
   */
  private scrollToTop(): void {
    const win = this.platform.window;
    if (!win) return;

    const currentUrl = this.router.url;

    // Try reciclar component
    if (currentUrl.includes('/reciclar')) {
      const reciclarComponent = (win as any).reciclarComponentInstance;
      if (reciclarComponent && typeof reciclarComponent.scrollToTop === 'function') {
        reciclarComponent.scrollToTop();
        return;
      }
    }

    // Try material component
    if (currentUrl.includes('/material')) {
      const materialComponent = (win as any).materialComponentInstance;
      if (materialComponent && typeof materialComponent.scrollToTop === 'function') {
        materialComponent.scrollToTop();
        return;
      }
    }

    // Try profile component
    if (currentUrl.includes('/profile')) {
      const profileComponent = (win as any).profileComponentInstance;
      if (profileComponent && typeof profileComponent.scrollToTop === 'function') {
        profileComponent.scrollToTop();
        return;
      }
    }

    // Fallback: use window scroll
    this.platform.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Toggle the 'unfolded' class on the navigation element (nano screens)
   * This expands/collapses the stacked tab navigation
   */
  toggleNavigation(event: Event): void {
    event.stopPropagation();
    const navigationEl = this.el.nativeElement.querySelector('.navigation');
    if (navigationEl) {
      const hasUnfolded = navigationEl.classList.contains('unfolded');
      if (hasUnfolded) {
        this.renderer.removeClass(navigationEl, 'unfolded');
      } else {
        this.renderer.addClass(navigationEl, 'unfolded');
      }
    }
  }

  ngOnInit() {
    // Initialize materials state translations
    materialsState.initTranslations(this.translationService);

    // Block indexing for non-production environments
    if (!environment.production) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
      console.log('🚫 SEO: noindex applied (non-production environment)');
    }

    // Initialize SEO service
    this.seoService.setSeoData('Remateriar', 'Mapa colaborativo de reciclaje y reutilización de materiales. Encontrá puntos cercanos, centros verdes y contenedores en tu ciudad.');

    // Initialize theme service (automatically loads saved preference)
    console.log('🎨 Theme service initialized:', this.themeService.currentTheme());

    // Initialize blur on scroll (dismiss keyboard when scrolling)
    this.blurOnScrollService.init();

    // Auth check (token refresh + session validation) now runs automatically
    // inside AuthService.constructor() → checkAuthOnInit(), exposed as authReady$.
    // No need to call refreshTokenOnInit() here — it was causing race conditions
    // with API requests that fire before the refresh completes.

    // Initialize navigation service
    this.swnav.initializeNavigation();
    this.swnav.suscribeBackButton();

    // Initialize materials from API (uses HttpClient for interceptor support)
    fetchAndPopulateMaterials(this.http);

    // Initialize desktop viewport signal
    this.initializeViewportSignal();

    // Listen for viewport changes (browser navigation bar hide/show)
    this.setupViewportChangeListener();

    // Initialize font preferences from localStorage
    this.initializeFontPreferences();

    // Initialize push notifications (FCM)
    this.initializePushNotifications();

    // Subscribe to router events to update navbar visibility
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.navbarVisibility.updateVisibilityForPath(event.urlAfterRedirects);
    });

    /*     console.log("ngOnInit()");
        const ionContent = document.querySelector('.ion-page');
        console.log("ionContent", ionContent);
        ionContent?.addEventListener('ionScroll', (ev: any) => {
          console.log("ionScroll");
          window.scrollTo(0, ev.detail.scrollTop);
        }); */
  }

  private initializeViewportSignal(): void {
    if (!this.platform.isBrowser) return;
/*     document.addEventListener('contextmenu', function (e) {
      e.preventDefault();
    }); */
    // Set initial viewport state
    isDesktopViewport.set(this.platform.innerWidth >= this.desktopBreakpoint);

    console.log('📱 Desktop viewport signal initialized:', {
      currentWidth: this.platform.innerWidth,
      breakpoint: this.desktopBreakpoint,
      isDesktop: isDesktopViewport()
    });
  }

  private setupViewportChangeListener(): void {
    if (!this.platform.isBrowser) return;

    // Store initial viewport height
    this.initialViewportHeight = this.platform.innerHeight;
    // Listen for viewport changes
    let timeout: any;
    this.platform.addWindowListener('resize', () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        this.handleViewportChange.bind(this);
        // tu código aquí
      }, 200); // o 200ms
    });

    // Also listen for orientation changes
    this.platform.addWindowListener('orientationchange', () => {
      // Small delay to ensure orientation change is complete
      setTimeout(() => {
        this.handleViewportChange();
      }, 100);
    });

    console.log('📱 Viewport change listener initialized. Initial height:', this.initialViewportHeight);
  }

  private handleViewportChange(): void {
    if (!this.platform.isBrowser) return;

    const currentHeight = this.platform.innerHeight;
    const currentWidth = this.platform.innerWidth;
    const heightDifference = this.initialViewportHeight - currentHeight;
    const isNavigationBarVisible = heightDifference > 50; // Threshold for detecting nav bar

    // Update desktop viewport signal
    const wasDesktop = isDesktopViewport();
    const isDesktop = currentWidth >= this.desktopBreakpoint;
    if (wasDesktop !== isDesktop) {
      isDesktopViewport.set(isDesktop);
      console.log('📱 Desktop viewport changed:', { wasDesktop, isDesktop, currentWidth, breakpoint: this.desktopBreakpoint });
    }

    // Update CSS custom property for viewport height
    const docEl = this.platform.documentElement;
    if (docEl) {
      docEl.style.setProperty('--vh', `${currentHeight * 0.01}px`);
    }

    // Add/remove class based on navigation bar visibility
    const body = this.platform.body;
    if (body) {
      if (isNavigationBarVisible) {
        body.classList.add('nav-bar-visible');
        body.classList.remove('nav-bar-hidden');
      } else {
        body.classList.add('nav-bar-hidden');
        body.classList.remove('nav-bar-visible');
      }
    }

    console.log('📱 Viewport change detected:', {
      currentHeight,
      currentWidth,
      initialHeight: this.initialViewportHeight,
      heightDifference,
      isNavigationBarVisible,
      isDesktop,
      vhUnit: `${currentHeight * 0.01}px`
    });

    // Dispatch custom event for components to listen to
    this.platform.dispatchWindowEvent(new CustomEvent('viewportChange', {
      detail: {
        currentHeight,
        currentWidth,
        initialHeight: this.initialViewportHeight,
        heightDifference,
        isNavigationBarVisible,
        isDesktop
      }
    }));
  }

  // Initialize font preferences from storage
  private initializeFontPreferences(): void {
    const body = this.platform.body;
    if (body) {
      // Migrate old localStorage keys to new format
      this.storage.migrateOldKeys();

      // Load saved font family preference
      const savedFontFamily = this.storage.get<string>('ui', STORAGE_KEYS.ui.fontFamily, '');
      if (savedFontFamily) {
        // Remove all font family classes first
        const fontClasses = ['font-crimson', 'font-system', 'font-raleway', 'font-serif', 'font-sans', 'font-monospace'];
        fontClasses.forEach(fontClass => body.classList.remove(fontClass));

        // Add the saved font family class
        body.classList.add(savedFontFamily);
      } else {
        // Set default font family if none saved
        body.classList.add('font-crimson');
      }

      // Load saved font size preference
      const savedFontSize = this.storage.get<string>('ui', STORAGE_KEYS.ui.fontSize, '');
      if (savedFontSize) {
        // Remove all font size classes first
        const sizeClasses = ['font-size-xs', 'font-size-sm', 'font-size-md', 'font-size-lg', 'font-size-xl', 'font-size-xxl'];
        sizeClasses.forEach(sizeClass => body.classList.remove(sizeClass));

        // Add the saved font size class
        body.classList.add(savedFontSize);
      } else {
        // Set default font size if none saved
        body.classList.add('font-size-md');
      }

      console.log('🎨 Font preferences initialized:', {
        fontFamily: savedFontFamily || 'font-crimson (default)',
        fontSize: savedFontSize || 'font-size-md (default)'
      });
    }
  }

  /**
   * Initialize Firebase Cloud Messaging for push notifications
   */
  private initializePushNotifications(): void {
    // Only initialize if push notifications are supported
    if (!this.pushNotificationService.isSupported()) {
      console.log('📱 Push notifications not supported in this browser');
      return;
    }

    // Subscribe to incoming foreground messages
    this.pushNotificationService.message$.subscribe(message => {
      if (message) {
        console.log('📬 Push notification received:', message);
        // Refresh notification stats to update badge
        this.notificationService.refreshStats();
      }
    });

    // Subscribe to token changes and auto-register with backend
    this.pushNotificationService.token$.subscribe(token => {
      if (token) {
        console.log('🔑 FCM token available');
      }
    });

    // Subscribe to backend registration status
    this.pushNotificationService.registeredToken$.subscribe(token => {
      if (token) {
        console.log('✅ FCM token registered with backend');
      }
    });

    console.log('✅ Push notifications listeners initialized');
  }
}
